import { AxiosError } from "axios";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import * as React from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { ErrorBoundary } from "src/components/error-boundary";
import { TwoFactorModal } from "src/components/two-factor";
import { useSensitiveOperation } from "src/hooks/use-sensitive-operation";
import {
	AppLayout,
	type MenuItem,
	type MenuSection,
} from "src/layouts/app-layout";
import AccountSettingsPage from "src/pages/account-settings-page/account-settings-page";
import { AlertDetail, AlertsList } from "src/pages/alerts-page";
import {
	AMLTransactionDetail,
	AMLTransactionsList,
} from "src/pages/aml-transactions-page";
import { AuditLogDetail, AuditLogsLayout } from "src/pages/audit-logs-page";
import DashboardPage from "src/pages/dashboard-page/dashboard-page";
import ForgotPasswordPage from "src/pages/forgot-password-page/forgot-password-page";
import {
	BlockedWalletDetail,
	FundReturnDetail,
	FundReturnsList,
} from "src/pages/fund-returns-page";
import LoginPage from "src/pages/login-page/login-page";
import ManageCurrencySettingsPage from "src/pages/manage-currency-settings-page/manage-currency-settings-page";
import ManageInvitationsPage from "src/pages/manage-invitations-page/manage-invitations-page";
import ManageMerchantsPage from "src/pages/manage-merchants-page/manage-merchants-page";
import { OrderDetail, OrdersLayout } from "src/pages/manage-orders-page";
import { PaymentDetail, PaymentsLayout } from "src/pages/manage-payments-page";
import ManageSystemSettingsPage from "src/pages/manage-system-settings-page/manage-system-settings-page";
import {
	TransactionDetail,
	TransactionsLayout,
} from "src/pages/manage-transactions-page";
import { UserDetail, UsersLayout } from "src/pages/manage-users-page";
import {
	InboundWalletsList,
	OutboundWalletsList,
	SuspendedWalletsList,
	WalletDetail,
	WalletsLayout,
	WalletsRedirect,
} from "src/pages/manage-wallets-page";
import { MerchantDetailPage } from "src/pages/merchant-detail-page";
import OTPVerificationPage from "src/pages/otp-verification-page/otp-verification-page";
import {
	AllWithdrawalsList,
	PendingWithdrawalsList,
	PoolWithdrawalDetail,
	PoolWithdrawalsLayout,
} from "src/pages/pool-withdrawals-page";
import ResetPasswordPage from "src/pages/reset-password-page/reset-password-page";
import RevenuePage from "src/pages/revenue-page/revenue-page";
import Setup2FAPage from "src/pages/setup-2fa-page/setup-2fa-page";
import { TreasuryPage } from "src/pages/treasury-page";
import {
	UnattributedDepositDetail,
	UnattributedDepositsPage,
} from "src/pages/unattributed-deposits-page";
import type { Admin } from "src/providers/auth-provider";
import authProvider from "src/providers/auth-provider";
import { ENTITY_CONFIG } from "src/utils/entity-config";

const defaultMenus: MenuItem[] = [
	{
		path: "/dashboard",
		name: "Dashboard",
		icon: ENTITY_CONFIG.dashboard.icon,
		color: ENTITY_CONFIG.dashboard.color,
	},
	{
		path: "/revenue",
		name: "Revenue",
		icon: ENTITY_CONFIG.revenue.icon,
		color: ENTITY_CONFIG.revenue.color,
	},
	{
		path: "/treasury",
		name: "Treasury",
		icon: ENTITY_CONFIG.treasury.icon,
		color: ENTITY_CONFIG.treasury.color,
	},
	{
		path: "/users",
		name: "Users",
		icon: ENTITY_CONFIG.user.icon,
		color: ENTITY_CONFIG.user.color,
	},
	{
		path: "/merchants",
		name: "Merchants",
		icon: ENTITY_CONFIG.merchant.icon,
		color: ENTITY_CONFIG.merchant.color,
	},
	{
		path: "/wallets",
		name: "Wallets",
		icon: ENTITY_CONFIG.wallet.icon,
		color: ENTITY_CONFIG.wallet.color,
	},
	{
		path: "/transactions",
		name: "Transactions",
		icon: ENTITY_CONFIG.transaction.icon,
		color: ENTITY_CONFIG.transaction.color,
	},
	{
		path: "/orders",
		name: "Orders",
		icon: ENTITY_CONFIG.order.icon,
		color: ENTITY_CONFIG.order.color,
	},
	{
		path: "/payments",
		name: "Payments",
		icon: ENTITY_CONFIG.payment.icon,
		color: ENTITY_CONFIG.payment.color,
	},
	{
		path: "/invitations",
		name: "Invitations",
		icon: ENTITY_CONFIG.invitation.icon,
		color: ENTITY_CONFIG.invitation.color,
	},
	{
		path: "/alerts",
		name: "Alerts",
		icon: ENTITY_CONFIG.alert.icon,
		color: ENTITY_CONFIG.alert.color,
	},
	{
		path: "/aml-transactions",
		name: "AML Review",
		icon: ENTITY_CONFIG.aml.icon,
		color: ENTITY_CONFIG.aml.color,
	},
	{
		path: "/fund-returns",
		name: "Fund Returns",
		icon: ENTITY_CONFIG.fund_return.icon,
		color: ENTITY_CONFIG.fund_return.color,
	},
	{
		path: "/unattributed-deposits",
		name: "Unattributed Deposits",
		icon: ENTITY_CONFIG.orphan_transaction.icon,
		color: ENTITY_CONFIG.orphan_transaction.color,
	},
	{
		path: "/pool-withdrawals",
		name: "Pool Withdrawals",
		icon: ENTITY_CONFIG.pool_withdrawal.icon,
		color: ENTITY_CONFIG.pool_withdrawal.color,
	},
	{
		path: "/audit-logs",
		name: "Audit Logs",
		icon: ENTITY_CONFIG.audit_log.icon,
		color: ENTITY_CONFIG.audit_log.color,
	},
	{
		path: "/settings/system",
		name: "System Settings",
		icon: ENTITY_CONFIG.system_settings.icon,
		color: ENTITY_CONFIG.system_settings.color,
	},
	{
		path: "/settings/currency",
		name: "Currency Settings",
		icon: ENTITY_CONFIG.currency_settings.icon,
		color: ENTITY_CONFIG.currency_settings.color,
	},
];

const menuSections: MenuSection[] = [
	{
		title: "Overview",
		items: [
			{
				path: "/dashboard",
				name: "Dashboard",
				icon: ENTITY_CONFIG.dashboard.icon,
				color: ENTITY_CONFIG.dashboard.color,
			},
			{
				path: "/revenue",
				name: "Revenue",
				icon: ENTITY_CONFIG.revenue.icon,
				color: ENTITY_CONFIG.revenue.color,
			},
			{
				path: "/treasury",
				name: "Treasury",
				icon: ENTITY_CONFIG.treasury.icon,
				color: ENTITY_CONFIG.treasury.color,
			},
		],
	},
	{
		title: "Operations",
		items: [
			{
				path: "/orders",
				name: "Orders",
				icon: ENTITY_CONFIG.order.icon,
				color: ENTITY_CONFIG.order.color,
			},
			{
				path: "/payments",
				name: "Payments",
				icon: ENTITY_CONFIG.payment.icon,
				color: ENTITY_CONFIG.payment.color,
			},
			{
				path: "/transactions",
				name: "Transactions",
				icon: ENTITY_CONFIG.transaction.icon,
				color: ENTITY_CONFIG.transaction.color,
			},
			{
				path: "/wallets",
				name: "Wallets",
				icon: ENTITY_CONFIG.wallet.icon,
				color: ENTITY_CONFIG.wallet.color,
			},
			{
				path: "/pool-withdrawals",
				name: "Pool Withdrawals",
				icon: ENTITY_CONFIG.pool_withdrawal.icon,
				color: ENTITY_CONFIG.pool_withdrawal.color,
			},
		],
	},
	{
		title: "Compliance",
		items: [
			{
				path: "/aml-transactions",
				name: "AML Review",
				icon: ENTITY_CONFIG.aml.icon,
				color: ENTITY_CONFIG.aml.color,
			},
			{
				path: "/fund-returns",
				name: "Fund Returns",
				icon: ENTITY_CONFIG.fund_return.icon,
				color: ENTITY_CONFIG.fund_return.color,
			},
			{
				path: "/unattributed-deposits",
				name: "Unattributed Deposits",
				icon: ENTITY_CONFIG.orphan_transaction.icon,
				color: ENTITY_CONFIG.orphan_transaction.color,
			},
			{
				path: "/audit-logs",
				name: "Audit Logs",
				icon: ENTITY_CONFIG.audit_log.icon,
				color: ENTITY_CONFIG.audit_log.color,
			},
		],
	},
	{
		title: "Management",
		items: [
			{
				path: "/merchants",
				name: "Merchants",
				icon: ENTITY_CONFIG.merchant.icon,
				color: ENTITY_CONFIG.merchant.color,
			},
			{
				path: "/users",
				name: "Users",
				icon: ENTITY_CONFIG.user.icon,
				color: ENTITY_CONFIG.user.color,
			},
			{
				path: "/invitations",
				name: "Invitations",
				icon: ENTITY_CONFIG.invitation.icon,
				color: ENTITY_CONFIG.invitation.color,
			},
		],
	},
	{
		title: "System",
		items: [
			{
				path: "/alerts",
				name: "Alerts",
				icon: ENTITY_CONFIG.alert.icon,
				color: ENTITY_CONFIG.alert.color,
			},
			{
				path: "/settings/system",
				name: "System Settings",
				icon: ENTITY_CONFIG.system_settings.icon,
				color: ENTITY_CONFIG.system_settings.color,
			},
			{
				path: "/settings/currency",
				name: "Currency Settings",
				icon: ENTITY_CONFIG.currency_settings.icon,
				color: ENTITY_CONFIG.currency_settings.color,
			},
		],
	},
];

interface AppLoadState {
	reloadAdminInfo?: boolean;
}

const PUBLIC_ROUTES = [
	"/verify-otp",
	"/setup-2fa",
	"/forgot-password",
	"/reset-password",
	"/login",
];

const App: React.FC = () => {
	const location = useLocation();
	const state = location.state as AppLoadState | undefined;
	const posthog = usePostHog();
	const navigate = useNavigate();
	const { theme } = useTheme();

	const {
		isOpen: is2FAModalOpen,
		modalDetails,
		handleVerify: handle2FAVerify,
		handleCancel: handle2FACancel,
	} = useSensitiveOperation();

	const [admin, setAdmin] = React.useState<Admin | undefined>(undefined);
	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const loadAdminInfo = React.useCallback(async () => {
		const abortController = new AbortController();

		try {
			await authProvider.getCookie();

			if (abortController.signal.aborted) return;

			const adminData = await authProvider.getMe();

			if (!abortController.signal.aborted) {
				setAdmin(adminData);
			}
		} catch (e) {
			if (abortController.signal.aborted) return;

			console.error("Failed to load admin:", e);

			if (e instanceof AxiosError && e.response?.status === 401) {
				setAdmin(undefined);
				navigate("/login", {
					state: {
						isNeedLogout: true,
					},
				});
			}
		} finally {
			if (!abortController.signal.aborted) {
				setIsLoading(false);
			}
		}

		return () => abortController.abort();
	}, [navigate]);

	// Load admin info on mount if not on public route
	React.useEffect(() => {
		const isPublicRoute = PUBLIC_ROUTES.some((route) =>
			location.pathname.startsWith(route),
		);

		if (!isPublicRoute) {
			let cancelled = false;

			loadAdminInfo().then((cleanup) => {
				if (cancelled && cleanup) cleanup();
			});

			return () => {
				cancelled = true;
			};
		}

		// On public routes, set loading to false
		setIsLoading(false);
	}, [loadAdminInfo, location.pathname.startsWith]); // Only run on mount

	// Reload admin info when state changes
	React.useEffect(() => {
		if (state?.reloadAdminInfo) {
			let cancelled = false;

			loadAdminInfo().then((cleanup) => {
				if (cancelled && cleanup) cleanup();
			});

			return () => {
				cancelled = true;
			};
		}
	}, [state?.reloadAdminInfo, loadAdminInfo]);

	// PostHog identification
	React.useEffect(() => {
		if (admin) {
			posthog?.identify(admin.email, {
				email: admin.email,
				uuid: admin.uuid,
			});
		}
	}, [posthog, admin]);

	// Handle navigation and document title
	React.useEffect(() => {
		if (location.pathname === "/") {
			navigate("/dashboard", { state: location.state, replace: true });
			return;
		}

		if (location.pathname === "/login") {
			document.title = "Login | Admin | Monbits Pay";
			return;
		}

		const pageName = defaultMenus.find(
			(item) => item.path === location.pathname,
		)?.name;

		if (pageName) {
			document.title = `${pageName} | Admin | Monbits Pay`;
		}
	}, [location.pathname, location.state, navigate]);

	const logout = React.useCallback(async () => {
		try {
			posthog?.reset(true);
			await authProvider.logout();
			setAdmin(undefined);
			navigate("/login", {
				state: {
					isNeedLogout: true,
				},
			});
		} catch (error) {
			console.error("Logout failed:", error);
			// Force logout on client side even if API fails
			setAdmin(undefined);
			navigate("/login", {
				state: {
					isNeedLogout: true,
				},
			});
		}
	}, [posthog, navigate]);

	return (
		<ErrorBoundary>
			<Toaster
				position="bottom-right"
				richColors
				theme={theme as "light" | "dark" | "system"}
			/>
			<TwoFactorModal
				open={is2FAModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						handle2FACancel();
					}
				}}
				onVerify={handle2FAVerify}
				title={modalDetails.title}
				description={modalDetails.description}
				showBackupOption={true}
			/>
			<Routes>
				<Route path="login" element={<LoginPage />} />
				<Route path="verify-otp" element={<OTPVerificationPage />} />
				<Route path="setup-2fa" element={<Setup2FAPage />} />
				<Route path="forgot-password" element={<ForgotPasswordPage />} />
				<Route path="reset-password" element={<ResetPasswordPage />} />
				<Route
					path="*"
					element={
						<AppLayout
							menus={defaultMenus}
							menuSections={menuSections}
							user={admin}
							onLogout={logout}
							showMerchantSelector={false}
							loading={isLoading}
						>
							<Routes>
								<Route path="dashboard" element={<DashboardPage />} />
								<Route path="revenue" element={<RevenuePage />} />
								<Route path="treasury" element={<TreasuryPage />} />
								<Route path="users" element={<UsersLayout />}>
									<Route path=":userId" element={<UserDetail />} />
								</Route>
								<Route path="merchants" element={<ManageMerchantsPage />} />
								<Route
									path="merchants/:merchantId"
									element={<MerchantDetailPage />}
								/>
								<Route path="wallets">
									<Route index element={<WalletsRedirect />} />
									<Route element={<WalletsLayout />}>
										<Route path="outbound" element={<OutboundWalletsList />}>
											<Route path=":walletId" element={<WalletDetail />} />
										</Route>
										<Route path="inbound" element={<InboundWalletsList />}>
											<Route path=":walletId" element={<WalletDetail />} />
										</Route>
										<Route
											path="suspended"
											element={<SuspendedWalletsList />}
										/>
									</Route>
								</Route>
								<Route path="transactions" element={<TransactionsLayout />}>
									<Route
										path=":transactionId"
										element={<TransactionDetail />}
									/>
								</Route>
								<Route path="orders" element={<OrdersLayout />}>
									<Route path=":orderId" element={<OrderDetail />} />
								</Route>
								<Route path="payments" element={<PaymentsLayout />}>
									<Route path=":paymentId" element={<PaymentDetail />} />
								</Route>
								<Route path="invitations" element={<ManageInvitationsPage />} />
								<Route path="alerts" element={<AlertsList />}>
									<Route path=":alertId" element={<AlertDetail />} />
								</Route>
								<Route
									path="aml-transactions"
									element={<AMLTransactionsList />}
								>
									<Route
										path=":transactionId"
										element={<AMLTransactionDetail />}
									/>
								</Route>
								<Route path="fund-returns" element={<FundReturnsList />}>
									<Route
										path="wallets/:walletId/:assetSymbol"
										element={<BlockedWalletDetail />}
									/>
									<Route path=":id" element={<FundReturnDetail />} />
								</Route>
								<Route
									path="unattributed-deposits"
									element={<UnattributedDepositsPage />}
								>
									<Route path=":id" element={<UnattributedDepositDetail />} />
								</Route>
								<Route
									path="pool-withdrawals"
									element={<PoolWithdrawalsLayout />}
								>
									<Route
										index
										element={
											<Navigate to="/pool-withdrawals/pending" replace />
										}
									/>
									<Route path="pending" element={<PendingWithdrawalsList />}>
										<Route
											path=":withdrawalId"
											element={<PoolWithdrawalDetail />}
										/>
									</Route>
									<Route path="all" element={<AllWithdrawalsList />}>
										<Route
											path=":withdrawalId"
											element={<PoolWithdrawalDetail />}
										/>
									</Route>
									<Route
										path=":withdrawalId"
										element={<PoolWithdrawalDetail />}
									/>
								</Route>
								<Route path="audit-logs" element={<AuditLogsLayout />}>
									<Route path=":logId" element={<AuditLogDetail />} />
								</Route>
								<Route
									path="settings/system"
									element={<ManageSystemSettingsPage />}
								/>
								<Route
									path="settings/currency"
									element={<ManageCurrencySettingsPage />}
								/>
								<Route
									path="account-settings"
									element={<AccountSettingsPage />}
								/>
								<Route path="*" element={<div>Not found</div>} />
							</Routes>
						</AppLayout>
					}
				/>
			</Routes>
		</ErrorBoundary>
	);
};

export default App;
